/**
 * Cold War's sizing answer as the editor reads it. The endpoint types `status` as free text and
 * `violation` as a map from rule name to value; this is the one place that turns both into closed
 * cases, so a status or a rule the editor does not know is decided here and nowhere else.
 */
import type { PositionSizingResponse } from "@eu.mdotm.api/sphere";

/** A trading rule Cold War applied to reach the shares it answered with. */
export type TradingRule =
	| { rule: "LOT_SIZE"; lot: number }
	| { rule: "DENOMINATION_MIN"; minimum: number }
	| { rule: "DENOMINATION_INCREMENT"; increment: number }
	| { rule: "MIN_INITIAL_SUBSCRIPTION"; amount: number }
	| { rule: "UNKNOWN"; name: string; value: number };

type SizedValue = { ticker?: string; value?: number; weight?: number };

/**
 * `OK` sized the instrument: with its shares and the price they came from, and the rules applied to
 * reach them. Any other status says why Cold War did not size it in shares; it may still answer
 * with a market value and a weight (`./sizing`). `NOT_SHAREABLE` sized it by value, so it names the
 * rules applied to reach that value too.
 */
export type SizingAnswer =
	| (SizedValue & { status: "OK"; shares?: number; price?: number; priceDate?: string; rules: TradingRule[] })
	| (SizedValue & { status: "NOT_SHAREABLE"; rules: TradingRule[] })
	| (SizedValue & { status: "NO_PRICE" | "NOT_IN_MARKET_DATA" });

/** Thrown for a status the editor does not know: the sizing fails rather than guess what it means. */
export class UnknownSizingStatusError extends Error {
	constructor(status: string | undefined, ticker: string | undefined) {
		super(`Cold War sized ${ticker ?? "the position"} with the unknown status ${status ?? "(none)"}.`);
		this.name = "UnknownSizingStatusError";
	}
}

function tradingRule(name: string, value: number): TradingRule {
	switch (name) {
		case "lot_size":
			return { rule: "LOT_SIZE", lot: value };
		case "denomination_min":
			return { rule: "DENOMINATION_MIN", minimum: value };
		case "denomination_increment_amount":
			return { rule: "DENOMINATION_INCREMENT", increment: value };
		case "min_init_sub":
			return { rule: "MIN_INITIAL_SUBSCRIPTION", amount: value };
		default:
			return { rule: "UNKNOWN", name, value };
	}
}

/**
 * The rules named in a `violation` map, in the order Cold War gave them; none when it sent no map.
 * A rule at 0 constrains nothing: a bond without a denomination increment comes back with
 * `denomination_increment_amount: 0`.
 */
export function parseTradingRules(violation: Record<string, number> | null | undefined): TradingRule[] {
	return Object.entries(violation ?? {})
		.filter(([, value]) => value !== 0)
		.map(([name, value]) => tradingRule(name, value));
}

/** Reads Cold War's answer. Throws {@link UnknownSizingStatusError} for a status it does not know. */
export function parseSizingAnswer(response: PositionSizingResponse): SizingAnswer {
	const { status, ticker, value, weight } = response;
	switch (status) {
		case "OK":
			return {
				status,
				ticker,
				value,
				weight,
				shares: response.shares,
				price: response.price,
				priceDate: response.priceDate,
				rules: parseTradingRules(response.violation),
			};
		case "NOT_SHAREABLE":
			return { status, ticker, value, weight, rules: parseTradingRules(response.violation) };
		case "NO_PRICE":
		case "NOT_IN_MARKET_DATA":
			return { status, ticker, value, weight };
		default:
			throw new UnknownSizingStatusError(status, ticker);
	}
}
